import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Stack,
  Card,
  useTheme,
  alpha,
  Grid,
  List,
  ListItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as ActivateIcon,
  VpnKey as ResetPasswordIcon,
  AdminPanelSettings as AdminIcon,
  PhotoCamera as PhotoCameraIcon,
  Upload as UploadIcon,
  Close as CloseIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import axios from 'axios';
import { useSettings } from '../../../contexts/SettingsContext';
import AuthenticatedAvatar from '../../../components/AuthenticatedAvatar';

const API_URL = import.meta.env.VITE_API_URL || 'https://stardust-aims.amy.care';

interface AdminUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_active: boolean;
  is_verified: boolean;
  phone?: string;
  avatar_url?: string;
  created_at: string;
  last_login?: string;
}

interface Role {
  id: string;
  name: string;
  display_name: string;
  level: number;
  permission_ids: string[];
}

function AdminUserManagement() {
  const theme = useTheme();
  const { formatDateTime } = useSettings();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    password: '',
    avatar_url: '',
  });
  
  // Avatar upload states
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/settings/roles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRoles(response.data);
    } catch (err: any) {
      // Silently fail - roles are not critical for display
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_URL}/api/v1/admin/admin-users`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setUsers(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch admin users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedUser(null);
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      phone: '',
      password: '',
      avatar_url: '',
    });
    setAvatarFile(null);
    setAvatarPreview('');
    setEditDialogOpen(true);
  };

  const handleEdit = (user: AdminUser) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      phone: user.phone || '',
      password: '',
      avatar_url: user.avatar_url || '',
    });
    setAvatarFile(null);
    setAvatarPreview(user.avatar_url || '');
    setEditDialogOpen(true);
  };

  const handleDelete = (user: AdminUser) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadAvatar = async () => {
    if (!avatarFile) return formData.avatar_url;

    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      const uploadFormData = new FormData();
      uploadFormData.append('file', avatarFile);
      uploadFormData.append('document_type', 'profiles');

      const uploadResponse = await axios.post(`${API_URL}/api/v1/upload/`, uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      return uploadResponse.data.url;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload avatar');
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const handleStartCamera = async () => {
    try {
      console.log('[Camera] Starting camera...');
      setIsVideoReady(false);
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { width: { ideal: 1280 }, height: { ideal: 720 } } 
      });
      console.log('[Camera] Got stream:', stream.getTracks());
      setCameraStream(stream);
      setCameraActive(true);
    } catch (err) {
      console.error('[Camera] Error:', err);
      setError('Failed to access camera. Please check permissions.');
      setCameraActive(false);
    }
  };

  // Set video stream when camera becomes active
  useEffect(() => {
    if (cameraActive && cameraStream && videoRef.current) {
      console.log('[Camera] Setting srcObject in useEffect');
      videoRef.current.srcObject = cameraStream;
      console.log('[Camera] Setting timeout for ready state');
      // Set ready immediately - the video will start playing due to autoPlay attribute
      setTimeout(() => {
        console.log('[Camera] Timeout fired, setting ready');
        setIsVideoReady(true);
      }, 500);
    }
  }, [cameraActive, cameraStream]);

  const handleStopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
      setCameraActive(false);
      setIsVideoReady(false);
    }
  };

  const handleCapturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // Check if video has valid dimensions
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        setError('Camera not ready. Please wait a moment and try again.');
        return;
      }
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' });
            setAvatarFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
              setAvatarPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
            handleStopCamera();
          }
        }, 'image/jpeg', 0.95);
      }
    }
  };

  const handleDeleteAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview('');
    setFormData({ ...formData, avatar_url: '' });
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Upload avatar first if there's a new file
      let avatarUrl = formData.avatar_url;
      if (avatarFile) {
        avatarUrl = await uploadAvatar();
      }

      const data: any = {
        ...formData,
        avatar_url: avatarUrl,
        role: 'admin',
      };

      if (!formData.password) {
        delete data.password;
      }

      console.log('[Save User] Data being sent:', data);

      if (selectedUser) {
        await axios.put(`${API_URL}/api/v1/admin/admin-users/${selectedUser.id}`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API_URL}/api/v1/admin/admin-users`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      setEditDialogOpen(false);
      setAvatarFile(null);
      setAvatarPreview('');
      handleStopCamera();
      fetchUsers();
    } catch (err: any) {
      console.error('[Save User] Error:', err.response?.data);
      setError(err.response?.data?.detail || 'Failed to save admin user');
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/v1/admin/admin-users/${selectedUser.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete admin user');
    }
  };

  const handleToggleActive = async (user: AdminUser) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/v1/admin/admin-users/${user.id}/toggle-active`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to toggle admin user status');
    }
  };

  const handleResetPassword = async (user: AdminUser) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/v1/admin/admin-users/${user.id}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`New password for ${user.email}: ${response.data.new_password}\n\nPlease save this password and share it securely with the admin user.`);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleOpenRoleDialog = async (user: AdminUser) => {
    setSelectedUser(user);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/v1/settings/users/${user.id}/roles`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedRoleIds(response.data.role_ids || []);
    } catch (err) {
      setSelectedRoleIds([]);
    }
    setRoleDialogOpen(true);
  };

  const handleAssignRoles = async () => {
    if (!selectedUser) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/v1/settings/users/${selectedUser.id}/roles`,
        selectedRoleIds,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      setRoleDialogOpen(false);
      setSelectedUser(null);
      setSelectedRoleIds([]);
      await fetchUsers(); // Refresh
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to assign roles');
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AuthenticatedAvatar
            src={params.row.avatar_url}
            alt={`${params.row.first_name} ${params.row.last_name}`}
            sx={{ width: 32, height: 32 }}
          >
            <AdminIcon sx={{ color: theme.palette.error.main, fontSize: 20 }} />
          </AuthenticatedAvatar>
          <Typography variant="body2" fontWeight={500}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'first_name',
      headerName: 'First Name',
      width: 150,
    },
    {
      field: 'last_name',
      headerName: 'Last Name',
      width: 150,
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 150,
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'is_verified',
      headerName: 'Verified',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Yes' : 'No'}
          color={params.value ? 'info' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 180,
      renderCell: (params) => formatDateTime(params.value),
    },
    {
      field: 'last_login',
      headerName: 'Last Login',
      width: 180,
      renderCell: (params) => params.value ? formatDateTime(params.value) : '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 250,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.row)}
            sx={{ color: theme.palette.primary.main }}
            title="Edit"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleOpenRoleDialog(params.row)}
            sx={{ color: theme.palette.secondary.main }}
            title="Assign Roles"
          >
            <SecurityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleToggleActive(params.row)}
            sx={{ color: params.row.is_active ? theme.palette.warning.main : theme.palette.success.main }}
            title={params.row.is_active ? 'Deactivate' : 'Activate'}
          >
            {params.row.is_active ? <BlockIcon fontSize="small" /> : <ActivateIcon fontSize="small" />}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleResetPassword(params.row)}
            sx={{ color: theme.palette.info.main }}
            title="Reset Password"
          >
            <ResetPasswordIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row)}
            sx={{ color: theme.palette.error.main }}
            title="Delete"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight={600}>
              Admin User Management
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Manage administrator accounts with full panel access
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={{
              bgcolor: theme.palette.error.main,
              '&:hover': { bgcolor: theme.palette.error.dark },
            }}
          >
            Create Admin User
          </Button>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Card
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[1],
          }}
        >
          <DataGrid
            rows={users}
            columns={columns}
            loading={loading}
            autoHeight
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-row:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          />
        </Card>
      </Stack>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedUser ? 'Edit Admin User' : 'Create Admin User'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {/* Avatar Upload Section */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Profile Photo
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      border: `2px solid ${theme.palette.divider}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: theme.palette.grey[100],
                      position: 'relative',
                    }}
                  >
                    {avatarPreview ? (
                      <>
                        {avatarPreview.startsWith('blob:') || avatarPreview.startsWith('data:') ? (
                          <img
                            src={avatarPreview}
                            alt="Avatar"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <AuthenticatedAvatar
                            src={avatarPreview}
                            alt="Avatar"
                            sx={{ width: '100%', height: '100%' }}
                          >
                            <AdminIcon sx={{ fontSize: 60, color: theme.palette.grey[400] }} />
                          </AuthenticatedAvatar>
                        )}
                        <IconButton
                          size="small"
                          onClick={handleDeleteAvatar}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <AdminIcon sx={{ fontSize: 60, color: theme.palette.grey[400] }} />
                    )}
                  </Box>
                </Grid>
                <Grid item xs>
                  <Stack spacing={1}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleFileSelect}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || cameraActive}
                      fullWidth
                    >
                      Upload Photo
                    </Button>
                    {!cameraActive ? (
                      <Button
                        variant="outlined"
                        startIcon={<PhotoCameraIcon />}
                        onClick={handleStartCamera}
                        disabled={uploading}
                        fullWidth
                      >
                        Use Camera
                      </Button>
                    ) : (
                      <Stack spacing={1}>
                        <Box
                          sx={{
                            position: 'relative',
                            width: '100%',
                            borderRadius: 1,
                            overflow: 'hidden',
                            bgcolor: 'black',
                          }}
                        >
                          <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            style={{ width: '100%', display: 'block' }}
                          />
                          {!isVideoReady && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'rgba(0,0,0,0.7)',
                                color: 'white',
                              }}
                            >
                              Loading camera...
                            </Box>
                          )}
                        </Box>
                        <Button
                          variant="contained"
                          startIcon={<PhotoCameraIcon />}
                          onClick={handleCapturePhoto}
                          disabled={!isVideoReady}
                          fullWidth
                        >
                          {isVideoReady ? 'Capture Photo' : 'Waiting for camera...'}
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={handleStopCamera}
                          fullWidth
                        >
                          Cancel
                        </Button>
                      </Stack>
                    )}
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              disabled={!!selectedUser}
            />
            <TextField
              label="First Name"
              fullWidth
              required
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
            />
            <TextField
              label="Last Name"
              fullWidth
              required
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            />
            <TextField
              label="Phone"
              fullWidth
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
            <TextField
              label={selectedUser ? 'New Password (leave blank to keep current)' : 'Password'}
              type="password"
              fullWidth
              required={!selectedUser}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            />
            <Alert severity="info" sx={{ mt: 1 }}>
              This user will have full administrative access to the admin panel.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); handleStopCamera(); }}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            color="primary"
            disabled={uploading || cameraActive}
          >
            {uploading ? 'Uploading...' : selectedUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete admin user <strong>{selectedUser?.email}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Roles Dialog */}
      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Assign Roles to {selectedUser?.first_name} {selectedUser?.last_name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Select Roles
            </Typography>
            <List>
              {roles.map((role, index) => {
                // Handle both string and object IDs from backend
                const roleIdStr = role.id ? (typeof role.id === 'object' ? (role.id as any).$oid || String(role.id) : String(role.id)) : `temp-${index}`;
                console.log('Role ID:', role.name, '=', roleIdStr, 'Type:', typeof role.id);
                return (
                  <ListItem key={roleIdStr}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedRoleIds.includes(roleIdStr)}
                          onChange={(e) => {
                            console.log('Checkbox changed:', role.name, roleIdStr, e.target.checked);
                            if (e.target.checked) {
                              setSelectedRoleIds([...selectedRoleIds, roleIdStr]);
                            } else {
                              setSelectedRoleIds(selectedRoleIds.filter((id) => id !== roleIdStr));
                            }
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1">{role.display_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {role.name} (Level {role.level}) - {role.permission_ids.length} permissions
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAssignRoles}>
            Assign Roles
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminUserManagement;
